// UnifiedVideoPlayer drives the video previews on Transcriber, QA, Translator, and Lip QA
// screens, pairing the main video with optional dual audio tracks, subtitle overlays, and
// manual timestamp controls to keep reviewers aligned with dialogue timing.
"use strict";
// UnifiedVideoPlayer.js
import "react-video-seek-slider/styles.css";
import { VideoSeekSlider } from "react-video-seek-slider";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Grid, Stack, Typography, FormControlLabel, Checkbox } from "@mui/material";
import Slider from "@mui/material/Slider";
import VolumeDown from "@mui/icons-material/VolumeDown";
import VolumeUp from "@mui/icons-material/VolumeUp";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { primaryColor } from "../../utils/constants";
import { PLAYBACK_SPEED_OPTIONS } from "../../utils/playbackSpeedOptions";
import { parseTimestampAsTotalSeconds } from "../../utils/generalFunctions";
import { releaseMediaElement } from "../../utils/mediaElement";
import {
    DIALOGUE_HARD_SYNC_COOLDOWN_MS,
    DIALOGUE_SYNC_INTERVAL_MS,
    shouldCorrectDialogueDrift,
} from "../../utils/mediaPlaybackSync";
import InputMask from "react-input-mask";
import classes from "../../Assets/Styles/UnifiedVideoPlayer.module.css";

// Wait this long after the last slider movement before emitting onTimeSync
const SLIDER_TIME_SYNC_DELAY_MS = 400;

const UnifiedVideoPlayer = memo(({
    source,
    audioSource,
    seekTime,
    displayAudioTime,
    seekTimeIndex,
    setPausePlay,
    layout = 'vertical', // 'vertical' or 'horizontal'
    dualAudio = false,
    showDebug = false,
    enableScroll = false,
    // Slim controls bar: thinner seek bar, medium icons, small inputs.
    compactControls = false,
    customClasses = {},
    onTimeSync,
    // Also emit onTimeSync when the user seeks with the timeline slider
    syncOnSliderSeek = false,
    // Subtitle props
    showSubtitles = true,
    subtitles = [],
    showOriginalSubtitles = true,
    // Added props for checkboxes
    toggleOriginalSubtitles,
    toggleTranslatedSubtitles,
    originalLanguageDisplay = "Original",
    translatedLanguageDisplay = "Translated",
    onExportCSV = null,
    // Called when play is pressed while there is no source; playback starts
    // once the parent provides one.
    onPlayWithoutSource,
}) => {
    // State variables
    const [isBuffering, setIsBuffering] = useState(false);
    const videoRef = useRef(null);
    const dialoguePlayer = useRef(null);
    const animationFrameRef = useRef(null);
    const lastDialogueCorrectionAtRef = useRef(0);
    const playbackIntentRef = useRef(false);
    const startWhenLoadedRef = useRef(false);
    const pendingStartSecondsRef = useRef(0);
    const setPausePlayRef = useRef(setPausePlay);
    setPausePlayRef.current = setPausePlay;
    const pendingCanPlayHandlersRef = useRef(new Map());
    // Latest onTimeSync, so the debounced slider timer never calls a stale closure.
    const onTimeSyncRef = useRef(onTimeSync);
    onTimeSyncRef.current = onTimeSync;
    const sliderTimeSyncTimerRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [progress, setProgress] = useState(0);
    const [maxTime, setMaxTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(0.5);
    const [audioSpeed, setAudioSpeed] = useState(1);
    const [selectedAudioSource, setSelectedAudioSource] = useState('original');
    const [isSeeking, setIsSeeking] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [debugInfo, setDebugInfo] = useState({
        videoTime: 0,
        audioTime: 0,
        syncDiff: 0
    });
    const [currentSubtitle, setCurrentSubtitle] = useState(null);  // Translated subtitle (bottom)
    const [currentOriginalSubtitle, setCurrentOriginalSubtitle] = useState(null);  // Original subtitle (top)

    // Constants
    const DIALOGUE_OFFSET = 0;
    const isDialogueSelected = dualAudio && selectedAudioSource === 'dialogue';
    const [inputTime, setInputTime] = useState("");
    const [isEditing, setIsEditing] = useState(false);

    // Capture manual timestamp edits so we can seek after validation.
    const handleTimeInputChange = (e) => {
        const value = e.target.value;
        setInputTime(value);
        setIsEditing(true);
    };

    // Handle pressing Enter inside the timestamp field.
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            goToInputTime();
        }
    };

    // Seek to the time in the timestamp field (the current time unless the user
    // typed one) and emit onTimeSync so the page loads the matching dialogues,
    // like releasing the timeline slider.
    const goToInputTime = () => {
        const enteredTime = parseTimestampAsTotalSeconds(inputTime) * 1000;

        // Ignore a half-typed timestamp instead of seeking to NaN.
        if (!Number.isFinite(enteredTime)) {
            return;
        }

        const seekTime = Math.min(enteredTime, maxTime);
        const videoElement = videoRef.current;
        const dialogueElement = dialoguePlayer.current;
        const wasPaused = videoElement ? videoElement.paused : true;

        // Keep the video element aligned with the requested time.
        if (videoElement) {
            videoElement.currentTime = seekTime / 1000;
        }

        // Offset the dialogue audio to maintain sync with the video.
        if (dualAudio && dialogueElement) {
            dialogueElement.currentTime =
                (videoElement ? videoElement.currentTime : seekTime / 1000) -
                (isDialogueSelected ? DIALOGUE_OFFSET : 0);
        }

        setCurrentTime(seekTime);
        setIsEditing(false);

        // Restore whichever playback state the user previously had.
        if (wasPaused) {
            videoElement?.pause();
            if (isDialogueSelected && dialogueElement) {
                dialogueElement.pause();
            }
            setPausePlay(false);
            setIsPlaying(false);
        } else {
            attemptPlay(videoElement);
            if (isDialogueSelected && dialogueElement) {
                attemptPlay(dialogueElement);
            }
            setPausePlay(true);
            setIsPlaying(true);
        }

        // Emit the sync event so any parent components can react.
        if (onTimeSync) {
            onTimeSync(seekTime / 1000);
        }
    };

    // Update input time when video time changes
    useEffect(() => {
        if (!isEditing) {
            const formattedTime = new Date(currentTime).toISOString().substr(11, 12);
            setInputTime(formattedTime);
        }
    }, [currentTime, isEditing]);

    // Scroll handling for horizontal layout
    useEffect(() => {
        if (layout === 'horizontal' && enableScroll) {
            const handleScroll = () => {
                if (window.scrollY < 95) {
                    setIsScrolled(108 - window.scrollY);
                } else {
                    setIsScrolled(10);
                }
            };
            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
        }
    }, [layout, enableScroll]);

    // Time update handling
    // Mirror the video's time into state on each animation frame.
    const updateCurrentTime = useCallback(() => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime * 1000);
            animationFrameRef.current = requestAnimationFrame(updateCurrentTime);
        }
    }, []);

    // Begin our animation frame tracking when the video plays.
    const handlePlay = useCallback(() => {
        animationFrameRef.current = requestAnimationFrame(updateCurrentTime);
    }, [updateCurrentTime]);

    // Audio synchronization
    // Keep the dialogue track tightly coupled to the video's playback clock.
    const syncAudioWithVideo = useCallback(() => {
        if (isSeeking || !isDialogueSelected) {
            return;
        }

        const videoElement = videoRef.current;
        const dialogueElement = dialoguePlayer.current;

        if (videoElement && dialogueElement) {
            const videoTime = videoElement.currentTime;
            const targetAudioTime = videoTime - DIALOGUE_OFFSET;
            const audioTime = dialogueElement.currentTime;
            const timeDiff = Math.abs(audioTime - targetAudioTime);

            if (showDebug) {
                setDebugInfo({
                    videoTime: videoTime.toFixed(3),
                    audioTime: audioTime.toFixed(3),
                    syncDiff: timeDiff.toFixed(3)
                });
            }

            const now = Date.now();
            if (
                shouldCorrectDialogueDrift({
                    audioReadyState: dialogueElement.readyState,
                    audioTime,
                    isSeeking,
                    videoReadyState: videoElement.readyState,
                    videoTime: targetAudioTime,
                }) &&
                now - lastDialogueCorrectionAtRef.current >=
                    DIALOGUE_HARD_SYNC_COOLDOWN_MS
            ) {
                dialogueElement.currentTime = targetAudioTime;
                lastDialogueCorrectionAtRef.current = now;
            }
        }
    }, [isSeeking, isDialogueSelected, showDebug]);

    // Event handlers
    const handlePause = useCallback(() => {
        cancelAnimationFrame(animationFrameRef.current);
    }, []);

    // Refresh duration and slider bounds when metadata becomes available.
    const handleDataLoaded = useCallback(() => {
        const videoElement = videoRef.current;
        const dialogueElement = dialoguePlayer.current;
        const duration = (videoElement && !Number.isNaN(videoElement.duration) ? videoElement.duration : null)
            ?? (dialogueElement && !Number.isNaN(dialogueElement.duration) ? dialogueElement.duration : null);

        if (duration) {
            setMaxTime(duration * 1000);
        }
    }, []);

    // Track how much of the active media element is buffered.
    const handleProgress = useCallback((event) => {
        const target = event?.target;
        const mediaElement = target || (isDialogueSelected ? dialoguePlayer.current : videoRef.current);

        if (mediaElement) {
            const buffer = mediaElement.buffered;
            if (buffer.length > 0) {
                let currentBuffer = 0;
                const inSeconds = mediaElement.currentTime;
                for (let i = 0; i < buffer.length; i++) {
                    if (buffer.start(i) <= inSeconds && inSeconds <= buffer.end(i)) {
                        currentBuffer = i;
                        break;
                    }
                }
                setProgress(buffer.end(currentBuffer) * 1000 || 0);
            }
        }
    }, [isDialogueSelected]);

    // Play attempt with error handling
    // Attempt to play a media element, retrying once metadata is ready if necessary.
    const cancelPendingPlay = useCallback((mediaElement) => {
        const pendingHandler = pendingCanPlayHandlersRef.current.get(mediaElement);

        if (pendingHandler) {
            mediaElement.removeEventListener("canplay", pendingHandler);
            pendingCanPlayHandlersRef.current.delete(mediaElement);
        }
    }, []);

    const cancelAllPendingPlays = useCallback(() => {
        pendingCanPlayHandlersRef.current.forEach((handler, mediaElement) => {
            mediaElement.removeEventListener("canplay", handler);
        });
        pendingCanPlayHandlersRef.current.clear();
    }, []);

    const attemptPlay = useCallback(async (mediaElement) => {
        if (!mediaElement) return;

        cancelPendingPlay(mediaElement);

        try {
            if (mediaElement.readyState >= 3) {
                await mediaElement.play();
                if (!playbackIntentRef.current) {
                    mediaElement.pause();
                }
            } else {
                const handleCanPlayOnce = async () => {
                    pendingCanPlayHandlersRef.current.delete(mediaElement);
                    mediaElement.removeEventListener("canplay", handleCanPlayOnce);

                    if (!playbackIntentRef.current) {
                        return;
                    }

                    try {
                        await mediaElement.play();
                        if (!playbackIntentRef.current) {
                            mediaElement.pause();
                        }
                    } catch (error) {
                        console.error("Error playing media after canplay:", error);
                    }
                };
                pendingCanPlayHandlersRef.current.set(mediaElement, handleCanPlayOnce);
                mediaElement.addEventListener("canplay", handleCanPlayOnce);
            }
        } catch (error) {
            console.error("Error attempting to play media:", error);
        }
    }, [cancelPendingPlay]);

    // Pause the other clock whenever either active element buffers. Allowing
    // the video to advance while dialogue audio is stalled creates a seek loop.
    const handleWaiting = useCallback((event) => {
        setIsBuffering(true);
        if (!isDialogueSelected) {
            return;
        }

        if (event?.currentTarget === dialoguePlayer.current) {
            videoRef.current?.pause();
        } else {
            dialoguePlayer.current?.pause();
        }
    }, [isDialogueSelected]);

    // Resume playback after buffering resolves for whichever source is active.
    const handleCanPlay = useCallback(() => {
        if (isBuffering) {
            const videoElement = videoRef.current;
            const dialogueElement = dialoguePlayer.current;
            if (
                isDialogueSelected &&
                (videoElement?.readyState < 3 || dialogueElement?.readyState < 3)
            ) {
                return;
            }

            setIsBuffering(false);
            if (playbackIntentRef.current) {
                if (isDialogueSelected && dialogueElement) {
                    dialogueElement.currentTime = videoElement?.currentTime || 0;
                    Promise.all([
                        attemptPlay(videoElement),
                        attemptPlay(dialogueElement),
                    ]);
                } else {
                    attemptPlay(videoElement);
                }
            }
        }
    }, [attemptPlay, isBuffering, isDialogueSelected]);

    // Playback controls
    // Toggle playback while respecting whichever audio source is active.
    const togglePlayPause = async () => {
        const videoElement = videoRef.current;
        const dialogueElement = dialoguePlayer.current;

        if (!source && onPlayWithoutSource && !isPlaying) {
            startWhenLoadedRef.current = true;
            onPlayWithoutSource();
            return;
        }

        const pauseVideo = () => {
            try {
                videoElement?.pause();
            } catch (error) {
                console.error("Error pausing video:", error);
            }
        };

        const pauseDialogue = () => {
            if (!dialogueElement) return;
            try {
                dialogueElement.pause();
            } catch (error) {
                console.error("Error pausing audio:", error);
            }
        };

        // When the dialogue source is selected, coordinate both players.
        if (dualAudio && isDialogueSelected) {
            if (isPlaying) {
                playbackIntentRef.current = false;
                cancelAllPendingPlays();
                pauseDialogue();
                pauseVideo();
                setPausePlay(false);
                setIsPlaying(false);
            } else {
                playbackIntentRef.current = true;
                if (videoElement && dialogueElement) {
                    dialogueElement.currentTime = videoElement.currentTime;
                }
                await Promise.all([
                    attemptPlay(videoElement),
                    attemptPlay(dialogueElement),
                ]);
                setPausePlay(true);
                setIsPlaying(true);
            }
            return;
        }

        // Otherwise simply toggle the video's playback, pausing dialogue if present.
        if (isPlaying) {
            playbackIntentRef.current = false;
            cancelAllPendingPlays();
            pauseVideo();
            if (dualAudio) {
                pauseDialogue();
            }
            setPausePlay(false);
            setIsPlaying(false);
            return;
        }

        try {
            playbackIntentRef.current = true;
            await attemptPlay(videoElement);
            if (isDialogueSelected && dialogueElement) {
                await attemptPlay(dialogueElement);
            }
            setPausePlay(true);
            setIsPlaying(true);
        } catch (error) {
            console.error("Error playing media:", error);
        }
    };

    // Control handlers
    // Apply volume changes uniformly to every active element.
    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);

        if (videoRef.current) {
            videoRef.current.volume = newVolume;
        }

        if (dialoguePlayer.current) {
            dialoguePlayer.current.volume = newVolume;
        }
        setVolume(newVolume);
    };

    // Keep playback rate adjustments in sync between players.
    const handleSpeedChange = (e) => {
        const newSpeed = parseFloat(e.target.value);
        if (videoRef.current) {
            videoRef.current.playbackRate = newSpeed;
        }
        if (dialoguePlayer.current) {
            dialoguePlayer.current.playbackRate = newSpeed;
        }
        setAudioSpeed(newSpeed);
    };

    // Swap between original and dialogue audio while preserving sync.
    const handleAudioSourceChange = async (e) => {
        if (!dualAudio) return;

        const newSource = e.target.value;
        const wasPlaying = isPlaying;
        const videoElement = videoRef.current;
        const dialogueElement = dialoguePlayer.current;
        const videoTime = videoElement ? videoElement.currentTime : 0;

        // Pause both elements so we can realign them before switching.
        playbackIntentRef.current = false;
        cancelAllPendingPlays();
        if (dialogueElement) {
            try {
                dialogueElement.pause();
            } catch (error) {
                console.error("Error pausing audio during source change:", error);
            }
        }
        if (videoElement) {
            try {
                videoElement.pause();
            } catch (error) {
                console.error("Error pausing video during source change:", error);
            }
        }

        setIsPlaying(false);
        setPausePlay(false);
        setSelectedAudioSource(newSource);

        setTimeout(async () => {
            const freshVideo = videoRef.current || videoElement;
            const freshDialogue = dialoguePlayer.current || dialogueElement;

            // Restore timing and playback attributes after the DOM updates.
            if (freshVideo) {
                freshVideo.currentTime = videoTime;
                freshVideo.playbackRate = audioSpeed;
                freshVideo.volume = volume;
                freshVideo.muted = newSource === "dialogue";
            }

            if (freshDialogue) {
                freshDialogue.currentTime = videoTime - DIALOGUE_OFFSET;
                freshDialogue.playbackRate = audioSpeed;
                freshDialogue.volume = volume;
                if (newSource !== "dialogue") {
                    freshDialogue.pause();
                }
            }

            if (!wasPlaying) {
                return;
            }

            playbackIntentRef.current = true;

            if (newSource === "dialogue") {
                await Promise.all([
                    attemptPlay(freshVideo),
                    attemptPlay(freshDialogue),
                ]);
            } else {
                await attemptPlay(freshVideo);
            }

            setIsPlaying(true);
            setPausePlay(true);
        }, 0);
    };

    // Seek both media elements when the user drags the timeline slider.
    const handleTimeChange = (time) => {
        const newTime = time / 1000;
        const videoElement = videoRef.current;
        const dialogueElement = dialoguePlayer.current;

        if (!videoElement && !dialogueElement) {
            return;
        }

        setIsSeeking(true);

        if (videoElement) {
            const offset = isDialogueSelected ? DIALOGUE_OFFSET : 0;
            videoElement.currentTime = newTime + offset;
        }

        if (dualAudio && dialogueElement) {
            dialogueElement.currentTime = newTime;
        }

        setCurrentTime(time);

        // The slider fires continuously while dragging; sync once the user settles.
        if (syncOnSliderSeek) {
            clearTimeout(sliderTimeSyncTimerRef.current);
            sliderTimeSyncTimerRef.current = setTimeout(() => {
                onTimeSyncRef.current?.(newTime);
            }, SLIDER_TIME_SYNC_DELAY_MS);
        }
    };

    useEffect(() => () => clearTimeout(sliderTimeSyncTimerRef.current), []);

    // Effect for seeking completion
    useEffect(() => {
        const handleSeeked = () => {
            setIsSeeking(false);
        };

        const mediaElements = [videoRef.current, dialoguePlayer.current].filter(Boolean);

        mediaElements.forEach(element => {
            element.addEventListener('seeked', handleSeeked);
        });

        return () => {
            mediaElements.forEach(element => {
                element.removeEventListener('seeked', handleSeeked);
            });
        };
    }, [dualAudio]);

    // Effect for player event listeners
    useEffect(() => {
        const players = [videoRef.current, dialoguePlayer.current].filter(Boolean);
        players.forEach(player => {
            if (player) {
                player.addEventListener("play", handlePlay);
                player.addEventListener("pause", handlePause);
                player.addEventListener("loadeddata", handleDataLoaded);
                player.addEventListener("progress", handleProgress);
            }
        });

        return () => {
            players.forEach(player => {
                if (player) {
                    player.removeEventListener("play", handlePlay);
                    player.removeEventListener("pause", handlePause);
                    player.removeEventListener("loadeddata", handleDataLoaded);
                    player.removeEventListener("progress", handleProgress);
                }
            });
            cancelAnimationFrame(animationFrameRef.current);
        };
    }, [handlePlay, handlePause, handleDataLoaded, handleProgress, dualAudio]);

    // Effect for time display
    useEffect(() => {
        displayAudioTime((currentTime / 1000).toFixed(3));
    }, [currentTime, displayAudioTime]);

    // Ensure both media elements always reflect the current volume level.
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.volume = volume;
        }

        if (dialoguePlayer.current) {
            dialoguePlayer.current.volume = volume;
        }
    }, [volume, dualAudio]);

    // Effect for external seek actions
    useEffect(() => {
        if (seekTimeIndex > 0 && seekTime >= 0) {

            if (videoRef.current) {
                // Set seeking state to true
                setIsSeeking(true);

                // Set the video time
                pendingStartSecondsRef.current = seekTime;
                videoRef.current.currentTime = seekTime;

                if (dualAudio && dialoguePlayer.current) {
                    dialoguePlayer.current.currentTime = seekTime - (isDialogueSelected ? DIALOGUE_OFFSET : 0);
                }

                // Update UI time
                setCurrentTime(seekTime * 1000);
            }
        }
    }, [seekTime, seekTimeIndex, dualAudio, isDialogueSelected]);

    // Effect for playback sync
    useEffect(() => {
        let syncInterval;

        if (isPlaying && !isSeeking && isDialogueSelected) {
            syncInterval = setInterval(
                syncAudioWithVideo,
                DIALOGUE_SYNC_INTERVAL_MS
            );
        }

        return () => {
            if (syncInterval) {
                clearInterval(syncInterval);
            }
        };
    }, [isPlaying, isSeeking, syncAudioWithVideo, isDialogueSelected]);

    // Layout-specific classes and styles
    const containerStyles = layout === 'horizontal'
        ? {
            width: '100%',
            top: `${isScrolled}px`,
            position: 'sticky',
            zIndex: 1000,
            ...customClasses.containerStyles
        }
        : customClasses.containerStyles || {};

    // Keep the hidden dialogue element from drifting or playing when not selected.
    useEffect(() => {
        if (!dualAudio || !dialoguePlayer.current) {
            return;
        }

        const dialogueElement = dialoguePlayer.current;
        const videoElement = videoRef.current;

        if (selectedAudioSource !== "dialogue") {
            dialogueElement.pause();
            if (videoElement && !Number.isNaN(videoElement.currentTime)) {
                dialogueElement.currentTime = videoElement.currentTime;
            }
        }
    }, [dualAudio, selectedAudioSource]);

    // Provide lightweight listeners when we're in single-audio mode.
    useEffect(() => {
        if (!dualAudio && videoRef.current) {
            // Set up video-only playback handling
            const videoElement = videoRef.current;

            const handleVideoTimeUpdate = () => {
                setCurrentTime(videoElement.currentTime * 1000);
            };

            const handleVideoLoadedMetadata = () => {
                setMaxTime(videoElement.duration * 1000);
            };

            videoElement.addEventListener('timeupdate', handleVideoTimeUpdate);
            videoElement.addEventListener('loadedmetadata', handleVideoLoadedMetadata);

            return () => {
                videoElement.removeEventListener('timeupdate', handleVideoTimeUpdate);
                videoElement.removeEventListener('loadedmetadata', handleVideoLoadedMetadata);
            };
        }
    }, [dualAudio]);

    // A signed URL can arrive after this player mounts, especially when returning
    // to a screen through client-side navigation. Reload the media element when
    // that URL changes so the browser does not retain a detached/empty resource.
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) {
            return undefined;
        }

        videoElement.pause();
        playbackIntentRef.current = false;
        cancelAllPendingPlays();
        cancelAnimationFrame(animationFrameRef.current);
        setIsPlaying(false);
        setIsBuffering(false);
        setCurrentTime(0);
        setProgress(0);
        setMaxTime(0);

        if (source) {
            videoElement.src = source;
        } else {
            videoElement.removeAttribute("src");
        }
        videoElement.load();

        if (source && startWhenLoadedRef.current) {
            startWhenLoadedRef.current = false;
            const startSeconds = pendingStartSecondsRef.current;
            videoElement.currentTime = startSeconds;
            setCurrentTime(startSeconds * 1000);
            playbackIntentRef.current = true;
            attemptPlay(videoElement).then(() => {
                setPausePlayRef.current?.(true);
                setIsPlaying(true);
            });
        }

        return () => {
            playbackIntentRef.current = false;
            cancelAllPendingPlays();
            releaseMediaElement(videoElement);
            cancelAnimationFrame(animationFrameRef.current);
        };
    }, [attemptPlay, cancelAllPendingPlays, source]);

    // Capture the hidden audio element while the ref is still attached. React can
    // clear refs before passive unmount cleanup runs, so reading the ref from the
    // cleanup itself can leave a media request alive after route navigation.
    useEffect(() => {
        const dialogueElement = dialoguePlayer.current;
        if (!dialogueElement) {
            return undefined;
        }

        if (audioSource) {
            dialogueElement.src = audioSource;
        } else {
            dialogueElement.removeAttribute("src");
        }
        dialogueElement.load();

        return () => releaseMediaElement(dialogueElement);
    }, [audioSource, dualAudio]);

    // Helper function to convert subtitle time format to seconds
    const timeStringToSeconds = (timeString) =>
        parseTimestampAsTotalSeconds(timeString);

    // Effect for translated subtitle handling (bottom)
    useEffect(() => {
        if (!showSubtitles || !subtitles || subtitles.length === 0) {
            setCurrentSubtitle(null);
            return;
        }

        // Check for subtitle that should be displayed at current time
        const videoTimeInSeconds = currentTime / 1000;

        // Find the subtitle that matches the current timestamp
        const activeSubtitle = subtitles.find(subtitle => {
            // Convert time strings (HH:MM:SS,mmm) to seconds
            const startTime = timeStringToSeconds(subtitle.startTime);
            const endTime = timeStringToSeconds(subtitle.endTime);

            return videoTimeInSeconds >= startTime && videoTimeInSeconds <= endTime;
        });

        setCurrentSubtitle(activeSubtitle);
    }, [currentTime, showSubtitles, subtitles]);

    // Effect for original language subtitle handling (top)
    useEffect(() => {
        if (!showOriginalSubtitles || !subtitles || subtitles.length === 0) {
            setCurrentOriginalSubtitle(null);
            return;
        }

        // Check for subtitle that should be displayed at current time
        const videoTimeInSeconds = currentTime / 1000;

        // Find the subtitle that matches the current timestamp using the same timing as translated subtitles
        const activeSubtitle = subtitles.find(subtitle => {
            // Convert time strings (HH:MM:SS,mmm) to seconds
            const startTime = timeStringToSeconds(subtitle.startTime);
            const endTime = timeStringToSeconds(subtitle.endTime);

            return videoTimeInSeconds >= startTime && videoTimeInSeconds <= endTime;
        });

        // Create an originalSubtitle object using the original text from the same subtitle entry
        if (activeSubtitle && activeSubtitle.originalText) {
            setCurrentOriginalSubtitle({
                ...activeSubtitle,
                text: activeSubtitle.originalText,
            });
        } else {
            setCurrentOriginalSubtitle(null);
        }
    }, [currentTime, showOriginalSubtitles, subtitles]);

    // Sizes for the slim controls bar; undefined keeps the regular look.
    const controlIconSize = compactControls ? "medium" : "large";
    const compactInputStyle = compactControls
        ? { height: 28, lineHeight: "28px", fontSize: 14, boxSizing: "border-box" }
        : undefined;
    // White labels: the theme's dark text is unreadable on the dark controls bar.
    const compactLabelSx = compactControls
        ? { "& .MuiFormControlLabel-label": { fontSize: 14, color: "#fff" } }
        : undefined;

    return (
        <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={layout === 'horizontal' ? 12 : 5.85}
            xl={layout === 'horizontal' ? 12 : 5.85}
            container
            direction="row"
            height={layout === 'horizontal' ? 'auto' : '715px'}
            style={containerStyles}
            className={customClasses.container}
        >
            <Grid
                item
                container
                className={customClasses.videoContainer}
                sx={{ position: 'relative' }}
            >
                <video
                    ref={videoRef}
                    preload="metadata"
                    width="100%"
                    height="100%"
                    muted={isDialogueSelected}
                    onWaiting={handleWaiting}
                    onCanPlay={handleCanPlay}
                    onPlaying={handleCanPlay}
                    style={{ backgroundColor: '#000' }}
                >
                    <source type="video/mp4" />
                </video>

                {/* Original subtitle overlay (top) */}
                {showOriginalSubtitles && currentOriginalSubtitle && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '5%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            maxWidth: '80%',
                            padding: '8px 16px',
                            backgroundColor: 'rgba(0, 0, 0, 0.45)',
                            color: '#fff',
                            borderRadius: "var(--radius-md)",
                            textAlign: 'center',
                            fontSize: 'var(--font-size-header)',
                            zIndex: 10,
                            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)'
                        }}
                    >
                        {currentOriginalSubtitle.text}
                    </div>
                )}

                {/* Translated subtitle overlay (bottom) */}
                {showSubtitles && currentSubtitle && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '5%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            maxWidth: '80%',
                            padding: '8px 16px',
                            backgroundColor: 'rgba(0, 0, 0, 0.45)',
                            color: '#fff',
                            borderRadius: "var(--radius-md)",
                            textAlign: 'center',
                            fontSize: 'var(--font-size-header)',
                            zIndex: 10,
                            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)'
                        }}
                    >
                        {currentSubtitle.text}
                    </div>
                )}

                {dualAudio && (
                    <audio
                        ref={dialoguePlayer}
                        preload="metadata"
                        style={{ display: 'none' }}
                        onWaiting={handleWaiting}
                        onCanPlay={handleCanPlay}
                        onPlaying={handleCanPlay}
                    />
                )}
            </Grid>

            {showDebug && (
                <Grid item container padding={1} style={{ background: '#f5f5f5' }}>
                    <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                        Source: <strong>{selectedAudioSource}</strong> |
                        Video Time: {debugInfo.videoTime}s |
                        Audio Time: {debugInfo.audioTime}s |
                        Sync Diff: {debugInfo.syncDiff}s
                    </Typography>
                </Grid>
            )}

            {/* Controls Section */}
            <Grid
                item
                container
                className={customClasses.controlsContainer}
                sx={{ minHeight: compactControls ? 0 : '120px' }}
            >
                <Grid
                    paddingLeft={1}
                    paddingRight={1}
                    item
                    sx={{
                        width: '100%',
                        height: compactControls ? 12 : 20,
                        // Shrink the library's 18px hit area to fit the slimmer strip.
                        ...(compactControls && {
                            '& .ui-video-seek-slider .track .main': { height: '12px' },
                            '& .ui-video-seek-slider .thumb': { top: 0 },
                        }),
                    }}
                    bgcolor="#EBEBEB"
                >
                    <VideoSeekSlider
                        max={maxTime}
                        currentTime={currentTime}
                        bufferTime={progress}
                        onChange={handleTimeChange}
                        limitTimeTooltipBySides={true}
                        secondsPrefix="00:"
                        minutesPrefix="0:"
                    />
                </Grid>

                <Grid
                    item
                    container
                    direction="row"
                    alignItems="center"
                    gap={compactControls ? 2 : 1}
                    marginLeft={1}
                    marginBottom={compactControls ? 0 : 1}
                    sx={{
                        flexWrap: "wrap",
                        minHeight: compactControls ? 0 : "80px",
                        padding: compactControls ? "4px 8px" : "8px",
                        ...(compactControls && { fontSize: "14px" }),
                    }}
                >
                    {/* Rewind Button */}
                    <Grid
                        item
                        sx={{ color: `${primaryColor}`, cursor: "pointer", display: "flex" }}
                    >
                        <FastRewindIcon
                            fontSize={controlIconSize}
                            onClick={() => handleTimeChange(currentTime - 100)}
                        />
                    </Grid>

                    {/* Play/Pause Button */}
                    <Grid
                        item
                        sx={{ color: `${primaryColor}`, cursor: "pointer", display: "flex" }}
                    >
                        {isPlaying ? (
                            <PauseCircleIcon
                                fontSize={controlIconSize}
                                onClick={togglePlayPause}
                            />
                        ) : (
                            <PlayCircleIcon
                                fontSize={controlIconSize}
                                onClick={togglePlayPause}
                            />
                        )}
                    </Grid>

                    {/* Forward Button */}
                    <Grid
                        item
                        sx={{ color: `${primaryColor}`, cursor: "pointer", display: "flex" }}
                    >
                        <FastForwardIcon
                            fontSize={controlIconSize}
                            onClick={() => handleTimeChange(currentTime + 100)}
                        />
                    </Grid>

                    <Grid item sx={{ display: "flex" }}>
                        <InputMask
                            mask="99:99:99.99"
                            value={inputTime}
                            onChange={handleTimeInputChange}
                            onKeyPress={handleKeyPress}
                            className={classes["AudioWorkPlaceTimelineEditTranslation"]}
                            style={compactInputStyle}
                            placeholder="HH:MM:SS.MS"
                        />
                    </Grid>

                    {/* Jump the dialogue list to the time above, like releasing the slider. */}
                    {onTimeSync && (
                        <Grid item sx={{ display: "flex" }}>
                            <button
                                type="button"
                                className={classes["GoToTimeButton"]}
                                style={compactInputStyle}
                                onClick={goToInputTime}
                                title="Show the dialogues at this time"
                            >
                                Go to
                            </button>
                        </Grid>
                    )}

                    {/* Audio Source Selection */}
                    {dualAudio && (
                        <Grid item xs="auto">
                            <select
                                value={selectedAudioSource}
                                onChange={handleAudioSourceChange}
                                className={customClasses.selector}
                                style={compactInputStyle}
                            >
                                <option value="original">Original Audio</option>
                                <option value="dialogue">Dialogue Only</option>
                            </select>
                        </Grid>
                    )}

                    {/* Playback Speed Selection */}
                    <Grid>Speed:</Grid>
                    <Grid item xs="auto">
                        <select
                            value={audioSpeed}
                            onChange={handleSpeedChange}
                            className={customClasses.selector}
                            style={compactInputStyle}
                        >
                            {PLAYBACK_SPEED_OPTIONS.map(
                                ({ value, label }) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                )
                            )}
                        </select>
                    </Grid>

                    {/* Volume Control */}
                    <Grid item sx={{ width: 200 }}>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <VolumeDown />
                            <Slider
                                size={compactControls ? "small" : "medium"}
                                value={volume}
                                onChange={handleVolumeChange}
                                min={0}
                                max={1}
                                step={0.1}
                                aria-label="Volume"
                                sx={{ color: `${primaryColor}` }}
                            />
                            <VolumeUp />
                        </Stack>
                    </Grid>

                    {toggleOriginalSubtitles && (
                        <Grid item marginRight={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showOriginalSubtitles}
                                        onChange={toggleOriginalSubtitles}
                                        size={compactControls ? "small" : "medium"}
                                        sx={{
                                            color: 'white !important',
                                            ...(compactControls && { padding: '2px 6px' }),
                                        }}
                                    />
                                }
                                label={`${originalLanguageDisplay} Subtitles`}
                                sx={compactLabelSx}
                            />
                        </Grid>
                    )}

                    {toggleTranslatedSubtitles && (
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showSubtitles}
                                        onChange={toggleTranslatedSubtitles}
                                        size={compactControls ? "small" : "medium"}
                                        sx={{
                                            color: 'white !important',
                                            ...(compactControls && { padding: '2px 6px' }),
                                        }}
                                    />
                                }
                                label={`${translatedLanguageDisplay} Subtitles`}
                                sx={compactLabelSx}
                            />
                        </Grid>
                    )}

                    {onExportCSV && (
                        <Grid item sx={{ marginLeft: 'auto', marginRight: 2 }}>
                            <button
                                onClick={onExportCSV}
                                style={{
                                    padding: '8px 14px',
                                    backgroundColor: '#333',
                                    color: 'white',
                                    borderRadius: "var(--radius-md)",
                                    border: '1px solid #ccc',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                Export
                            </button>
                        </Grid>

                    )}
                </Grid>
            </Grid>
        </Grid>
    );
});

export default UnifiedVideoPlayer;
